/**
 * PrepaidCardsApiClient
 * 
 * Handles Bo Payments prepaid card operations
 */

import { BaseApiClient } from './BaseApiClient';
import type { CreateTapSessionRequest, CreateTapSessionResponse } from '@/types/prepaidTapSession';

export class PrepaidCardsApiClient extends BaseApiClient {
  /**
   * Issue a new prepaid card
   */
  async issueCard(amount: number, currency: string): Promise<any> {
    return this.post('/prepaid-card/issue', { amount, currency });
  }

  /**
   * Convert a token to a prepaid card
   */
  async convertTokenToPrepaidCard(tokenKey: string): Promise<any> {
    return this.post('/prepaid-card/convert-token', { tokenKey });
  }

  /**
   * Get prepaid card details for a token
   */
  async getPrepaidCardForToken(tokenKey: string): Promise<any> {
    try {
      const response = await this.get<{ prepaidCard: any }>(`/prepaid-card/token/${tokenKey}`);
      return response; // Return the full response which contains { prepaidCard: ... }
    } catch (error: any) {
      console.error('Error getting prepaid card for token:', error);
      if (error.response?.status === 404) {
        return null; // No prepaid card found for this token
      }
      throw error;
    }
  }

  /**
   * Get Bo Payments card balance
   */
  async getCardBalance(uniqueUserId: string): Promise<any> {
    return this.get(`/prepaid-card/balance/${uniqueUserId}`);
  }

  /**
   * Get Bo Payments card transactions
   */
  async getCardTransactions(uniqueUserId: string): Promise<any> {
    return this.get(`/prepaid-card/transactions/${uniqueUserId}`);
  }

  /**
   * Close a Bo Payments prepaid card
   */
  async closeCard(uniqueUserId: string, tokenId: string): Promise<any> {
    return this.post('/prepaid-card/close', { uniqueUserId, tokenId });
  }

  /**
   * Load funds onto a Bo Payments card
   */
  async loadCard(amount: number, description: string, tokenId: string): Promise<any> {
    if (!tokenId) {
      throw new Error('prepaid-card/load called without a tokenId');
    }
    return this.post('/prepaid-card/load', { amount, description, tokenId });
  }

  /**
   * Get Bo Payments VPPC configuration
   */
  async getConfig(): Promise<{
    enabled: boolean;
    simulationMode: boolean;
    baseUrl: string;
    limit: number;
    email: string;
  }> {
    return this.get('/prepaid-card/config');
  }

  /**
   * Get card details by card number
   */
  async getCardDetails(cardNumber: string): Promise<{
    cardNumber: string;
    cardHolderName: string;
    expiryDate: string;
    status: string;
    balance: number;
    currency: string;
    cardBrand: 'visa' | 'mastercard';
    lastUsed?: string;
  }> {
    return this.get(`/prepaid-card/details/${cardNumber}`);
  }

  /**
   * Block/unblock a prepaid card
   */
  async toggleCardStatus(uniqueUserId: string, tokenId: string, action: 'block' | 'unblock'): Promise<{
    success: boolean;
    status: string;
    message: string;
  }> {
    return this.post('/prepaid-card/toggle-status', { uniqueUserId, tokenId, action });
  }

  /**
   * Get card transaction history with filters
   */
  async getCardTransactionHistory(uniqueUserId: string, filters?: {
    startDate?: string;
    endDate?: string;
    type?: 'purchase' | 'refund' | 'load' | 'fee';
    status?: 'approved' | 'declined' | 'pending';
    limit?: number;
    offset?: number;
  }): Promise<{
    transactions: Array<{
      id: string;
      amount: number;
      currency: string;
      type: string;
      status: string;
      description: string;
      merchantName?: string;
      transactionDate: string;
      fees?: number;
    }>;
    total: number;
    hasMore: boolean;
  }> {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.type) params.append('type', filters.type);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());
    
    return this.get(`/prepaid-card/transactions/${uniqueUserId}?${params}`);
  }

  /**
   * Set card spending limits
   */
  async setCardLimits(uniqueUserId: string, tokenId: string, limits: {
    dailyLimit?: number;
    monthlyLimit?: number;
    transactionLimit?: number;
    atmLimit?: number;
  }): Promise<{
    success: boolean;
    message: string;
    limits: {
      dailyLimit: number;
      monthlyLimit: number;
      transactionLimit: number;
      atmLimit: number;
    };
  }> {
    return this.post('/prepaid-card/set-limits', { uniqueUserId, tokenId, limits });
  }

  /**
   * Get card spending limits
   */
  async getCardLimits(uniqueUserId: string): Promise<{
    dailyLimit: number;
    monthlyLimit: number;
    transactionLimit: number;
    atmLimit: number;
    dailySpent: number;
    monthlySpent: number;
  }> {
    return this.get(`/prepaid-card/limits/${uniqueUserId}`);
  }

  /**
   * Request physical card
   */
  async requestPhysicalCard(uniqueUserId: string, tokenId: string, shippingAddress: {
    name: string;
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  }): Promise<{
    success: boolean;
    requestId: string;
    estimatedDelivery: string;
    trackingNumber?: string;
  }> {
    return this.post('/prepaid-card/request-physical', { uniqueUserId, tokenId, shippingAddress });
  }

  /**
   * Get physical card status
   */
  async getPhysicalCardStatus(uniqueUserId: string): Promise<{
    status: 'not_requested' | 'requested' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
    requestDate?: string;
    trackingNumber?: string;
    estimatedDelivery?: string;
    shippingAddress?: {
      name: string;
      street: string;
      city: string;
      state: string;
      zipCode: string;
      country: string;
    };
  }> {
    return this.get(`/prepaid-card/physical-status/${uniqueUserId}`);
  }

  /**
   * Activate physical card
   */
  async activatePhysicalCard(uniqueUserId: string, tokenId: string, activationCode: string): Promise<{
    success: boolean;
    message: string;
    activatedAt: string;
  }> {
    return this.post('/prepaid-card/activate-physical', { uniqueUserId, tokenId, activationCode });
  }

  /**
   * Report card as lost or stolen
   */
  async reportCardLostOrStolen(uniqueUserId: string, tokenId: string, reason: 'lost' | 'stolen', description?: string): Promise<{
    success: boolean;
    reportId: string;
    newCardRequested: boolean;
    message: string;
  }> {
    return this.post('/prepaid-card/report-lost-stolen', { uniqueUserId, tokenId, reason, description });
  }

  /**
   * Get card fees structure
   */
  async getCardFees(): Promise<{
    issuanceFee: number;
    monthlyFee: number;
    transactionFee: number;
    atmFee: number;
    internationalFee: number;
    replacementFee: number;
    currency: string;
  }> {
    return this.get('/prepaid-card/fees');
  }

  /**
   * Validate card for transaction
   */
  async validateCard(uniqueUserId: string, amount: number, currency: string, merchantCategory?: string): Promise<{
    valid: boolean;
    reason?: string;
    availableBalance: number;
    fees: number;
    canProceed: boolean;
  }> {
    return this.post('/prepaid-card/validate', { uniqueUserId, amount, currency, merchantCategory });
  }

  /**
   * Start issuer-backed tap-to-pay session (opaque id for native shell only).
   */
  async createTapSession(body: CreateTapSessionRequest): Promise<CreateTapSessionResponse> {
    return this.post('/prepaid-card/tap-session', body);
  }
} 